import React from 'react';
import { Layers, ArrowRight, Cpu, Cog } from 'lucide-react';
import { useDbNavigate } from '../../context/DatabaseContext';

const toolGroups = [
  {
    id: 'plc-tools',
    label: 'PLC Tools',
    icon: Cpu,
    tools: [
      {
        slug: 'io-buffer-generation',
        label: 'IO Buffer Generation',
        description: 'Generates PLC import CSVs and structured text for input/output buffer programs from tag and platform data. Outputs PLC Import CSV, Input Buffer ST, and Output Buffer ST files.'
      },
      {
        slug: 'udt-comment-generator',
        label: 'UDT Comment Generation',
        description: 'Parses RSLogix 5000 tag CSV exports and generates comment rows for each UDT tag instance based on Tag XRef member definitions. Supports mode selection, range prompts, and array expansion.'
      },
      {
        slug: 'scada-tag-generation',
        label: 'SCADA Tag Generation',
        description: 'Generates Cimplicity and Ignition tag import files from Tool 2 CSV output. Produces CIM Import (140 cols), IGN First/Second Pass, and DataLog exports per SCADA system.'
      },
      {
        slug: 'simulation-tag-generation',
        label: 'Simulation Tag Generation',
        description: 'Generates SIM_ prefix tag sets for OTS (Over-the-Shoulder) testing from database or Tool 1 CSV input. Outputs PLC Import CSV, Input Buffer ST, and SCADA import (IGN or CIM). SIM tags are deleted post-testing by filtering on "SIM".'
      }
    ]
  },
  {
    id: 'configuration',
    label: 'Configuration',
    icon: Cog,
    tools: [
      {
        slug: 'plc-platforms',
        label: 'PLC Platforms',
        description: 'Manages PLC platform definitions used by the IO Buffer Generator. Defines raw AI/AO min/max values and platform metadata per project.'
      },
      {
        slug: 'plc-tag-xref',
        label: 'Tag XRef',
        description: 'Manages UDT datamember definitions and Tool2 custom flags. Drives the UDT Comment Generator — defines which members get included, their descriptions, and how they are handled.'
      }
    ]
  }
];

const IntegrationDashboardPage = () => {
  const navigate = useDbNavigate();
  const totalTools = toolGroups.reduce((sum, g) => sum + g.tools.filter(t => !t.future).length, 0);

  return (
    <div className="stui-dashboard-body">
      <div className="stui-dashboard-header">
        <Layers size={20} className="stui-dashboard-header-icon" />
        <h1 className="stui-dashboard-header-title">Integration</h1>
        <span className="stui-dashboard-header-subtitle">{totalTools} tools available</span>
      </div>

      <div className="stui-dashboard-groups">
        {toolGroups.map(group => {
          const GroupIcon = group.icon;
          const activeTools = group.tools.filter(t => !t.future);
          return (
            <div key={group.id}>
              <div className="stui-dashboard-group-header">
                <GroupIcon size={16} className="stui-dashboard-header-icon" />
                <span className="stui-dashboard-group-title">{group.label}</span>
                <span className="stui-dashboard-group-count">
                  {activeTools.length} {activeTools.length === 1 ? 'tool' : 'tools'} available
                </span>
              </div>
              <div className="stui-dashboard-tile-grid">
                {group.tools.map(tool => (
                  <div
                    key={tool.slug}
                    className={`stui-tile${tool.future ? ' disabled' : ' stui-clickable'}`}
                    onClick={() => !tool.future && navigate(`/${tool.slug}`)}
                  >
                    <div className="stui-tile-header">
                      <span className="stui-tile-title">{tool.label}</span>
                      {tool.future
                        ? <span className="stui-dashboard-tile-badge">Coming soon</span>
                        : <ArrowRight size={16} className="stui-dashboard-tile-action" />
                      }
                    </div>
                    <div className="stui-dashboard-tile-content">
                      <p className="stui-dashboard-tile-description">{tool.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default IntegrationDashboardPage;
